import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type TransitionEvent,
} from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

// ============================================================================
// Card slot-group definitions
//
// Each card is a curated, editable slot group so the theme owns the tone of
// the copy ("Weekend events", "City market", "Places to Explore" …) and the
// visuals (themed Unsplash stock photography). Every field is fully editable
// per-property from the inline editor, so property managers can drop in a
// property-specific photo and copy where it makes sense.
// ============================================================================

const card1Def: SlotGroupDefinition = {
  label: 'Highlight Card 1',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?auto=format&fit=crop&w=1200&q=80',
    },
    title: {
      label: 'Title',
      type: 'text',
      defaultValue: 'Weekend events',
    },
    blurb: {
      label: 'Description',
      type: 'text',
      defaultValue:
        'Live music, food, and community fun — all just steps away in a student-friendly neighborhood.',
    },
  },
};

const card2Def: SlotGroupDefinition = {
  label: 'Highlight Card 2',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://images.unsplash.com/photo-1488459716781-31db52582fe9?auto=format&fit=crop&w=1200&q=80',
    },
    title: {
      label: 'Title',
      type: 'text',
      defaultValue: 'City market',
    },
    blurb: {
      label: 'Description',
      type: 'text',
      defaultValue:
        'A short drive from home for a go-to destination of dining, shopping, and weekend hangouts in a lively, historic setting.',
    },
  },
};

const card3Def: SlotGroupDefinition = {
  label: 'Highlight Card 3',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://images.unsplash.com/photo-1445116572660-236099ec97a0?auto=format&fit=crop&w=1200&q=80',
    },
    title: {
      label: 'Title',
      type: 'text',
      defaultValue: 'Places to explore',
    },
    blurb: {
      label: 'Description',
      type: 'text',
      defaultValue:
        'From coffee shops and casual eateries to hidden gems and local favorites — the neighborhood is calling your name.',
    },
  },
};

const card4Def: SlotGroupDefinition = {
  label: 'Highlight Card 4',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=1200&q=80',
    },
    title: {
      label: 'Title',
      type: 'text',
      defaultValue: 'Everyday essentials',
    },
    blurb: {
      label: 'Description',
      type: 'text',
      defaultValue:
        'Groceries, fitness, and everything you need for the school-year grind — all within an easy walk or drive.',
    },
  },
};

// ============================================================================
// Component
// ============================================================================

interface CardData {
  image?: string;
  title?: string;
  blurb?: string;
}

interface CardEntry {
  path: string;
  def: SlotGroupDefinition;
  data: CardData;
}

/**
 * Peek-style carousel of curated neighborhood highlight cards. Each card is
 * an editable slot group so the theme's curated copy (Weekend events / City
 * market / …) and themed stock imagery can be tweaked per-property from the
 * inline editor. Loops infinitely via a three-copy render and silent scroll
 * reposition.
 */
export function NeighborhoodHighlights() {
  const card1 = useSlotGroup('neighborhoodhighlights.card_1', card1Def) as CardData;
  const card2 = useSlotGroup('neighborhoodhighlights.card_2', card2Def) as CardData;
  const card3 = useSlotGroup('neighborhoodhighlights.card_3', card3Def) as CardData;
  const card4 = useSlotGroup('neighborhoodhighlights.card_4', card4Def) as CardData;

  const allCards: CardEntry[] = [
    { path: 'neighborhoodhighlights.card_1', def: card1Def, data: card1 },
    { path: 'neighborhoodhighlights.card_2', def: card2Def, data: card2 },
    { path: 'neighborhoodhighlights.card_3', def: card3Def, data: card3 },
    { path: 'neighborhoodhighlights.card_4', def: card4Def, data: card4 },
  ];

  // Show any card that has at least a title (image may still be missing on
  // properties without a featured image — we fall back gracefully).
  const realCards = allCards.filter((c) => Boolean((c.data.title || '').trim()));

  const canLoop = realCards.length > 1;
  const displayed = canLoop ? [...realCards, ...realCards, ...realCards] : realCards;
  const setLen = realCards.length;

  // ────────────────────────────────────────────────────────────────────────
  // Transform-driven infinite carousel
  //
  // Native scroll + snap couldn't give a truly seamless loop — the browser's
  // scroll boundary and snap alignment kept producing visible artifacts when
  // wrapping. Instead we render three copies of the cards and drive position
  // with `transform: translateX(...)`. `virtualIndex` tracks the index (in
  // the 3× display array) of the card that should be centered. On next/prev
  // we bump virtualIndex by ±1 and the CSS transition animates the track.
  //
  // When the animation lands on a clone (virtualIndex outside [setLen,
  // 2·setLen − 1]) we teleport back to the equivalent middle-set index with
  // `transition: none`, which is visually identical (same card content) —
  // the user sees continuous forward or backward motion forever.
  // ────────────────────────────────────────────────────────────────────────

  const trackRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);

  const [virtualIndex, setVirtualIndex] = useState(setLen);
  const [withTransition, setWithTransition] = useState(false);
  const [metrics, setMetrics] = useState<{ step: number; cardWidth: number; viewportWidth: number }>(
    { step: 0, cardWidth: 0, viewportWidth: 0 },
  );

  // Reset virtualIndex to first card of the middle set whenever the number
  // of real cards changes (e.g. an editor adds a card).
  useEffect(() => {
    setVirtualIndex(setLen);
    // Position without animation on the next paint.
    setWithTransition(false);
  }, [setLen]);

  // Measure step (card + gap) and viewport width so translateX is exact.
  // Uses useLayoutEffect so the initial measurement happens before the first
  // browser paint, avoiding a flash of misaligned content.
  useLayoutEffect(() => {
    const track = trackRef.current;
    const viewport = viewportRef.current;
    if (!track || !viewport) return;

    const measure = () => {
      const cards = track.querySelectorAll<HTMLElement>('.nh-card');
      if (cards.length === 0) return;
      const c0 = cards[0];
      const cardWidth = c0.offsetWidth;
      let step = cardWidth;
      if (cards.length > 1) {
        step = cards[1].offsetLeft - c0.offsetLeft;
      }
      const viewportWidth = viewport.clientWidth;
      setMetrics((prev) => {
        if (
          prev.step === step &&
          prev.cardWidth === cardWidth &&
          prev.viewportWidth === viewportWidth
        ) {
          return prev;
        }
        return { step, cardWidth, viewportWidth };
      });
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(viewport);
    const firstCard = track.querySelector<HTMLElement>('.nh-card');
    if (firstCard) ro.observe(firstCard);
    return () => ro.disconnect();
  }, [setLen]);

  // Flag set true whenever we intentionally disable the transition to
  // teleport. Read by the "re-enable transition" effect below.
  const teleportPendingRef = useRef(false);

  // Enable transition on the frame AFTER the first measured paint. Double
  // rAF guarantees the browser has painted the initial translate=0 → measured
  // translate change with transition:none before we flip transitions on, so
  // the initial positioning never animates.
  useEffect(() => {
    if (metrics.step === 0) return;
    if (withTransition) return;
    if (teleportPendingRef.current) return;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setWithTransition(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [metrics.step, withTransition]);

  // translateX places the card at virtualIndex centered in the viewport.
  const translateX = useMemo(() => {
    const { step, cardWidth, viewportWidth } = metrics;
    if (step === 0) return 0;
    return viewportWidth / 2 - cardWidth / 2 - virtualIndex * step;
  }, [metrics, virtualIndex]);

  // When the transform lands on a clone card, silently teleport to the
  // equivalent middle-set index with no transition. Same visual content, so
  // the user perceives continuous motion.
  const handleTransitionEnd = useCallback(
    (e: TransitionEvent<HTMLDivElement>) => {
      if (!canLoop) return;
      if (e.propertyName !== 'transform') return;
      if (virtualIndex >= 2 * setLen) {
        teleportPendingRef.current = true;
        setWithTransition(false);
        setVirtualIndex((v) => v - setLen);
      } else if (virtualIndex < setLen) {
        teleportPendingRef.current = true;
        setWithTransition(false);
        setVirtualIndex((v) => v + setLen);
      }
    },
    [canLoop, virtualIndex, setLen],
  );

  // After a teleport (transition: none + virtualIndex changed), re-enable
  // the transition on the next paint so the following click animates again.
  useEffect(() => {
    if (!teleportPendingRef.current) return;
    if (withTransition) return;
    const raf1 = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        teleportPendingRef.current = false;
        setWithTransition(true);
      });
    });
    return () => cancelAnimationFrame(raf1);
  }, [withTransition, virtualIndex]);

  const advance = useCallback(
    (dir: 1 | -1) => {
      if (!setLen) return;
      // If we somehow arrived here with the transition still disabled from
      // a teleport, flip it back on before nudging virtualIndex.
      setWithTransition(true);
      setVirtualIndex((v) => v + dir);
    },
    [setLen],
  );

  return (
    <section className="neighborhood-highlights" aria-label="Neighborhood highlights">
      <div className="nh-inner">
        <RichTextContent
          path="neighborhoodhighlights.heading"
          label="Section Heading"
          defaultValue="<p>Explore the neighborhood</p>"
          as="h2"
          className="nh-heading"
        />

        {realCards.length === 0 ? (
          <p className="nh-empty">
            Add a title to at least one highlight card in the editor to populate this carousel.
          </p>
        ) : (
          <div className="nh-viewport" ref={viewportRef}>
            <button
              type="button"
              className="nh-nav nh-nav--prev"
              aria-label="Previous highlight"
              onClick={() => advance(-1)}
            >
              <span aria-hidden="true">&lsaquo;</span>
            </button>

            <div
              className="nh-track"
              ref={trackRef}
              style={
                {
                  transform: `translate3d(${translateX}px, 0, 0)`,
                  transition: withTransition
                    ? 'transform 520ms cubic-bezier(0.22, 0.61, 0.36, 1)'
                    : 'none',
                } as CSSProperties
              }
              onTransitionEnd={handleTransitionEnd}
            >
              {displayed.map((entry, i) => {
                const setIndex = canLoop ? Math.floor(i / realCards.length) : 0;
                const realIndex = canLoop ? i % realCards.length : i;
                const isReal = !canLoop || setIndex === 1;
                const { data, path, def } = entry;
                const cardBody = (
                  <>
                    <div className="nh-card-image-wrap">
                      {data.image ? (
                        <VibesitesImage
                          url={data.image}
                          alt={data.title || ''}
                          className="nh-card-image"
                          sizes="(min-width: 1280px) 720px, (min-width: 768px) 58vw, 78vw"
                        />
                      ) : (
                        <div className="nh-card-image-placeholder" aria-hidden="true" />
                      )}
                    </div>
                    <div className="nh-card-body">
                      <h3 className="nh-card-title">{data.title}</h3>
                      {data.blurb ? <p className="nh-card-blurb">{data.blurb}</p> : null}
                    </div>
                  </>
                );

                // Only the middle (real) set is wired to the inline editor —
                // the clone copies stay static so clicking on a peek doesn't
                // pop the editor for a hidden index.
                if (isReal) {
                  return (
                    <EditableSlotGroup
                      key={`real-${realIndex}`}
                      groupPath={path}
                      definition={def}
                      className="nh-card"
                    >
                      {cardBody}
                    </EditableSlotGroup>
                  );
                }

                return (
                  <div
                    key={`clone-${setIndex}-${realIndex}`}
                    className="nh-card"
                    aria-hidden="true"
                  >
                    {cardBody}
                  </div>
                );
              })}
            </div>

            <button
              type="button"
              className="nh-nav nh-nav--next"
              aria-label="Next highlight"
              onClick={() => advance(1)}
            >
              <span aria-hidden="true">&rsaquo;</span>
            </button>
          </div>
        )}
      </div>

      {/* Schema-lint anchors — hidden, never interactive. Ensures every
          schema slot-group has a matching wrapper somewhere in the tree even
          when a property drops down to fewer real cards. */}
      <EditableSlotGroup
        groupPath="neighborhoodhighlights.card_1"
        definition={card1Def}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="neighborhoodhighlights.card_2"
        definition={card2Def}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="neighborhoodhighlights.card_3"
        definition={card3Def}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="neighborhoodhighlights.card_4"
        definition={card4Def}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>

      <style>{`
        .neighborhood-highlights {
          background: #E5E5E5;
          padding: 3rem 0 3.5rem;
          overflow: hidden;
          /* Break out of any centered / max-width parent so the band spans
             the full viewport regardless of ancestor padding. */
          width: 100vw;
          margin-left: calc(50% - 50vw);
          margin-right: calc(50% - 50vw);
          box-sizing: border-box;
        }
        @media (min-width: 768px) {
          .neighborhood-highlights { padding: 4rem 0 4.5rem; }
        }

        .nh-inner {
          width: 100%;
          margin: 0 auto;
        }

        .nh-heading {
          font-family: "Raleway", var(--vibe-font-heading), sans-serif;
          font-size: 1.75rem;
          font-weight: 800;
          line-height: 1.1;
          letter-spacing: -0.005em;
          color: var(--vibe-color-text);
          text-align: center;
          margin: 0 0 2rem;
          padding: 0 1.25rem;
        }
        .nh-heading p,
        .nh-heading span {
          font: inherit;
          color: inherit;
          margin: 0;
        }
        @media (min-width: 768px) {
          .nh-heading { font-size: 2.25rem; margin-bottom: 2.5rem; }
        }
        @media (min-width: 1024px) {
          .nh-heading { font-size: 2.75rem; }
        }

        .nh-empty {
          font-family: var(--vibe-font-body);
          text-align: center;
          color: var(--vibe-color-text-muted);
          padding: 2rem 1.5rem;
          margin: 0;
        }

        .nh-viewport {
          position: relative;
          overflow: hidden;
          width: 100%;
          /* Viewport is the sliding window that clips the track. Padding is
             baked into the transform math (centering), not applied here. */
        }

        .nh-track {
          display: flex;
          gap: 1.25rem;
          padding: 0.25rem 0 1rem;
          width: max-content;
          will-change: transform;
        }
        @media (min-width: 768px) {
          .nh-track {
            gap: 1.75rem;
          }
        }

        .nh-card {
          flex: 0 0 min(78vw, 640px);
          display: flex;
          flex-direction: column;
          overflow: hidden;
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary, #ffffff);
          box-shadow: 0 14px 34px rgba(0, 0, 0, 0.14);
        }
        @media (min-width: 768px) {
          .nh-card { flex-basis: min(58vw, 720px); }
        }
        @media (min-width: 1280px) {
          .nh-card { flex-basis: 720px; }
        }

        .nh-card-image-wrap {
          width: 100%;
          aspect-ratio: 16 / 10;
          overflow: hidden;
          background: #ccc;
        }
        .nh-card-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .nh-card-image-placeholder {
          width: 100%;
          height: 100%;
          background:
            linear-gradient(135deg, rgba(255,255,255,0.15), rgba(0,0,0,0.15)),
            var(--vibe-color-primary);
        }

        .nh-card-body {
          padding: 1.5rem 1.75rem 1.75rem;
        }
        @media (min-width: 768px) {
          .nh-card-body { padding: 1.75rem 2.25rem 2rem; }
        }
        .nh-card-title {
          font-family: "Raleway", var(--vibe-font-heading), sans-serif;
          font-size: 1.5rem;
          font-weight: 700;
          line-height: 1.15;
          letter-spacing: -0.005em;
          color: inherit;
          margin: 0 0 0.5rem;
        }
        @media (min-width: 768px) {
          .nh-card-title { font-size: 1.875rem; margin-bottom: 0.75rem; }
        }
        .nh-card-blurb {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          line-height: 1.55;
          color: inherit;
          opacity: 0.92;
          margin: 0;
        }
        @media (min-width: 768px) {
          .nh-card-blurb { font-size: 1rem; }
        }

        .nh-nav {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          z-index: 3;
          width: 44px;
          height: 44px;
          border: none;
          background: #ffffff;
          color: var(--vibe-color-text);
          font-family: "Raleway", var(--vibe-font-heading), sans-serif;
          font-size: 1.5rem;
          font-weight: 500;
          line-height: 1;
          cursor: pointer;
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.18);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: background 0.2s ease, transform 0.2s ease, opacity 0.2s ease;
        }
        .nh-nav:hover {
          background: #f5f5f5;
          transform: translateY(-50%) scale(1.05);
        }
        .nh-nav:focus-visible {
          outline: 2px solid var(--vibe-color-primary);
          outline-offset: 3px;
        }
        /* Nav buttons hover near the peek edge of the centered card so they
           sit visually between the centered card and the adjacent peek. */
        .nh-nav--prev { left: 3vw; }
        .nh-nav--next { right: 3vw; }
        @media (min-width: 768px) {
          .nh-nav { width: 52px; height: 52px; font-size: 1.75rem; }
          .nh-nav--prev { left: 5vw; }
          .nh-nav--next { right: 5vw; }
        }
        @media (min-width: 1280px) {
          .nh-nav--prev { left: calc((100vw - min(58vw, 720px)) / 2 - 26px); }
          .nh-nav--next { right: calc((100vw - min(58vw, 720px)) / 2 - 26px); }
        }

        @media (prefers-reduced-motion: reduce) {
          /* Reduce the carousel's slide animation to a near-instant step —
             transitionend still fires (allowing the loop teleport), but the
             visible motion is minimal for users who prefer no animation. */
          .nh-track { transition-duration: 0.01ms !important; }
          .nh-nav { transition: none; }
          .nh-nav:hover { transform: translateY(-50%); }
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'NeighborhoodHighlights',
  displayName: 'Neighborhood Highlights',
  tokens: {
    'neighborhoodhighlights.heading': {
      label: 'Section Heading',
      type: 'richtext',
      defaultValue: '<p>Explore the neighborhood</p>',
    },
  },
  slotGroups: {
    'neighborhoodhighlights.card_1': card1Def,
    'neighborhoodhighlights.card_2': card2Def,
    'neighborhoodhighlights.card_3': card3Def,
    'neighborhoodhighlights.card_4': card4Def,
  },
};

(NeighborhoodHighlights as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default NeighborhoodHighlights;
